import { PAX_TYPE } from '@app/enums';
import { IROPS_STATUS } from '@app/enums/irops.enum';
import moment from 'moment';

const SEGMENT_STATUS = ['SC', 'WK'];
const IROPS_CONFIRMATION_ACTIONS = ['SCH2HK', 'CONFIT'];

/**
 * Verifica si la reservación tiene un remark de confirmación de IROPs
 * en genericSpecialRequests (SCH2HK o CONFIT).
 */
function hasIropsConfirmationRemark(pnr: any): boolean {
  const genericSpecialRequests = pnr?.genericSpecialRequests || [];
  if (!genericSpecialRequests.length) {
    return false;
  }

  return genericSpecialRequests.some((request) =>
    IROPS_CONFIRMATION_ACTIONS.some((action) =>
      (request?.freeText || '').toUpperCase().includes(action)
    )
  );
}

function validateIropsFlow(pnr: any, location?: string, hasConfirmedIrops = false): Boolean {
  const arrayCollection = pnr?.flightSummary?.segments;
  let arrayAfects = [];
  let isElegible = false;

  // Retorna false si ya confirmó en la sesión actual
  if (hasConfirmedIrops) {
    return false;
  }

  if (hasIropsConfirmationRemark(pnr)) {
    return false;
  }

  arrayAfects = arrayCollection.filter(
    (seg) => seg.isChangeOag || seg.isCancelOag || SEGMENT_STATUS.includes(seg.segmentStatus)
  );

  isElegible = arrayAfects.length > 0 ? true : false;

  return isElegible;
}

function validateIropsBanner(segments: Array<any>): boolean {
  return !!segments.find((seg) => {
    const { flightsNewInfo, iropEligibility } = seg;
    let exist = false;

    if (iropEligibility) {
      exist =
        iropEligibility?.iropAffected &&
        iropEligibility?.elegibilityToExchange &&
        flightsNewInfo?.status === IROPS_STATUS.SCHEDULED;
    }

    return exist;
  });
}

/* End: Validate Functions */

/* Parse Functions */

function getTime(dateTime): String {
  let time = '';

  if (dateTime != null) {
    let timeIndex = dateTime.indexOf('T');
    if (timeIndex != -1) {
      timeIndex++;
    }

    time = dateTime.substr(timeIndex, 5);
  }

  return time;
}

function calculateTimeFlight(departureOrigin, departureNew) {
  let newDate = moment(departureNew);
  let oldDate = moment(departureOrigin);

  let diferences = newDate.diff(oldDate, 'hours', true);
  let hours = Math.floor(diferences);

  const minutes = Math.round((diferences - hours) * 60);
  const time = moment().startOf('day').add(hours, 'hours').add(minutes, 'minutes');

  let hourDiff = time.hours();
  let minutesDiff = time.minutes();

  return {
    hours: hourDiff,
    minutes: minutesDiff,
    status: hourDiff > 0 || minutesDiff > 0 ? 'DELAYED' : 'AHEAD',
  };
}

function calculateTimeFlightwithType(departureOrigin, departureNew, status) {
  const newDate = moment(departureNew);
  const oldDate = moment(departureOrigin);
  const diferences = newDate.diff(oldDate, 'minutes');
  const absDifferences = Math.abs(diferences);

  const hours = Math.floor(absDifferences / 60);
  const minutes = absDifferences % 60;
  const finalStatus = status === IROPS_STATUS.DELAYED ? 'DELAYED' : 'AHEAD';

  return {
    hours,
    minutes,
    status: finalStatus,
  };
}

function convertMinutes(minutes) {
  const hours = Math.floor(minutes / 60);
  const restMinutes = minutes % 60;
  return `${hours}h ${restMinutes}m`;
}

function parseTime(departureDateTime, arrivalDateTime) {
  const separateDep = departureDateTime?.includes('T') ? 'T' : ' ';
  const separateArr = arrivalDateTime?.includes('T') ? 'T' : ' ';
  const splitDeparture = departureDateTime?.split(separateDep);
  const splitArrival = arrivalDateTime?.split(separateArr);

  const hourDeparture = splitDeparture?.[1]?.split(':') || '';
  const hourArrival = splitArrival?.[1]?.split(':') || '';

  return {
    departureTime: `${hourDeparture[0]}:${hourDeparture[1]}`,
    arrivalTime: `${hourArrival[0]}:${hourArrival[1]}`,
  };
}

function verifyChildAlone(passengers) {
  const filter = passengers.find((p) => p.paxType === PAX_TYPE.ADULT);
  return filter ? false : true;
}

/**
 * ETD desde Flight Status.
 * No usar flightsNewInfo.departureDateTime: en constructFlight guarda la original.
 */
function getFlightsNewInfoEstimatedDeparture(segment: any): string {
  const fni = segment?.flightsNewInfo;
  if (!fni) {
    return segment?.departureDateTime || '';
  }
  return fni.estimatedDepartureTime || segment?.departureDateTime || '';
}

/** Horario original de salida (Get Reservation / SABRE). */
function getSabreDepartureDate(segment: any): string {
  return segment?.departureDateTime || segment?.segment?.departureDateTime || '';
}

/** Horario original de llegada (Get Reservation / SABRE). */
function getSabreArrivalDate(segment: any): string {
  return segment?.arrivalDateTime || segment?.segment?.arrivalDateTime || '';
}

function isSegmentDelayedOrEarly(segment: any): boolean {
  const status = segment?.flightsNewInfo?.status;
  return (
    !!checkSegmentIsAffected(segment) &&
    (status === IROPS_STATUS.DELAYED || status === IROPS_STATUS.EARLY)
  );
}

/** Umbral (minutos) a partir del cual una afectación va al flujo IROPs (CVT20-13930). */
const IROPS_AFFECTATION_THRESHOLD_MINUTES = 45;

/** Decisión de navegación para un vuelo según su estatus de afectación. */
type FlightStatusDecision = 'terminal' | 'irops' | 'normal';

/**
 * Minutos de afectación tomados EXCLUSIVAMENTE de Flight Status (CVT20-13929/13931).
 * Prioridad: `departureDifferenceInMinutes`. Fallback: |estimated − original|, ambos de
 * Flight Status. Nunca usa el horario de SABRE (`segment.departureDateTime`).
 * Devuelve `null` cuando no hay datos válidos para no inventar un cero.
 */
function resolveAffectationMinutes(segment: any): number | null {
  const fni = segment?.flightsNewInfo;
  if (!fni) {
    return null;
  }

  const difference = fni.departureDifferenceInMinutes;
  if (typeof difference === 'number' && Number.isFinite(difference)) {
    return Math.abs(difference);
  }

  const estimated = fni.estimatedDepartureTime;
  const original = fni.originalDepartureTime;
  if (estimated && original) {
    const estimatedMs = new Date(estimated).getTime();
    const originalMs = new Date(original).getTime();
    if (Number.isFinite(estimatedMs) && Number.isFinite(originalMs)) {
      return Math.abs(Math.round((estimatedMs - originalMs) / 60000));
    }
  }

  return null;
}

/**
 * Tiempo de afectación para el badge/decisión. La magnitud viene de Flight Status
 * (`resolveAffectationMinutes`); `totalMinutes` es `null` cuando no hay datos válidos.
 * Mantiene `hours`/`minutes`/`status` para los consumidores existentes del badge.
 */
function calculateIropsAffectationTime(segment: any): {
  hours: number;
  minutes: number;
  status: 'DELAYED' | 'AHEAD';
  totalMinutes: number | null;
} {
  if (!segment) {
    return { hours: 0, minutes: 0, status: 'DELAYED', totalMinutes: null };
  }

  // Solo Early se etiqueta AHEAD; Delayed y cualquier otro estatus (Cancelled/Scheduled/
  // undefined que puede llegar desde el gate por currentSegment) caen a DELAYED para no
  // marcarlos incorrectamente como AHEAD. Los consumidores del badge solo pasan Delayed/Early.
  const status = segment?.flightsNewInfo?.status === IROPS_STATUS.EARLY ? 'AHEAD' : 'DELAYED';
  const totalMinutes = resolveAffectationMinutes(segment);

  if (totalMinutes === null) {
    return { hours: 0, minutes: 0, status, totalMinutes: null };
  }

  return {
    hours: Math.floor(totalMinutes / 60),
    minutes: totalMinutes % 60,
    status,
    totalMinutes,
  };
}

/**
 * Decisión de navegación pura para CI-21670 (CVT20-13930/13932). Sin Router ni servicios.
 * Matriz confirmada por LT:
 * - Cancelled → siempre terminal (ignora minutos, elegibilidad, showIrops, flightStatusAffectation).
 * - Delayed/Early: sin minutos válidos → terminal; |min| >= 45 → IROPs si está disponible, si no
 *   terminal (fallback, nunca flujo normal); |min| < 45 → terminal.
 * - Scheduled/OnTime/otros → flujo normal existente.
 */
function resolveFlightStatusNavigation(
  status: string | undefined,
  totalMinutes: number | null,
  iropsAvailable: boolean
): FlightStatusDecision {
  if (status === IROPS_STATUS.CANCELLED) {
    return 'terminal';
  }

  if (status === IROPS_STATUS.DELAYED || status === IROPS_STATUS.EARLY) {
    if (totalMinutes === null) {
      return 'terminal';
    }
    if (Math.abs(totalMinutes) >= IROPS_AFFECTATION_THRESHOLD_MINUTES) {
      return iropsAvailable ? 'irops' : 'terminal';
    }
    return 'terminal';
  }

  return 'normal';
}

function getSegmentDepartureDate(segment) {
  if (isSegmentDelayedOrEarly(segment)) {
    return getFlightsNewInfoEstimatedDeparture(segment);
  }

  return getSabreDepartureDate(segment);
}

function getSegmentArrivalDate(segment) {
  if (isSegmentDelayedOrEarly(segment)) {
    return (
      segment?.flightsNewInfo?.arrivalDateTime ||
      segment?.segment?.flightsNewInfo?.arrivalDateTime ||
      getSabreArrivalDate(segment)
    );
  }

  return getSabreArrivalDate(segment);
}

/* End: Parse Functions */

/* General Functions */

function checkSegmentIsAffected(segment) {
  return segment && segment?.flightsNewInfo;
}

function getFlightStatus(
  proposedDate: string,
  originalDate: string
): {
  hours: number;
  minutes: number;
  status: 'Delayed' | 'Early';
} {
  const newDate = new Date(proposedDate);
  const original = new Date(originalDate);

  const diffMs = newDate.getTime() - original.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const absMinutes = Math.abs(diffMinutes);
  const hours = Math.floor(absMinutes / 60);
  const minutes = absMinutes % 60;

  const status = diffMinutes > 5 ? 'Delayed' : 'Early';

  return {
    hours,
    minutes,
    status,
  };
}
/* End: General Functions */

export {
  getFlightStatus,
  validateIropsFlow,
  validateIropsBanner,
  getTime,
  calculateTimeFlight,
  calculateTimeFlightwithType,
  convertMinutes,
  parseTime,
  verifyChildAlone,
  getFlightsNewInfoEstimatedDeparture,
  getSabreDepartureDate,
  getSabreArrivalDate,
  isSegmentDelayedOrEarly,
  calculateIropsAffectationTime,
  resolveAffectationMinutes,
  resolveFlightStatusNavigation,
  IROPS_AFFECTATION_THRESHOLD_MINUTES,
  getSegmentDepartureDate,
  getSegmentArrivalDate,
  checkSegmentIsAffected,
};

export type { FlightStatusDecision };
